/**
 * One offering: its history, its sources, its evidence, and a calculator.
 *
 * An offering must be selected before anything is priced. Rates from different
 * sellers or tiers are never blended into a single line, which is why this page
 * is about an offering rather than about a model.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type {
  OfferingSummary,
  PriceRow,
  PriceWindow,
  SourceCapture,
  TokenBucket,
} from "@pricing/contracts";
import { client } from "../lib/api.ts";
import { contextParams } from "../lib/url-state.ts";
import { ContextBar } from "../components/ContextBar.tsx";
import { HistoryChart } from "../components/HistoryChart.tsx";
import { EstimateCalculator } from "../components/EstimateCalculator.tsx";
import { TextFilter, validateInstant } from "../components/TextFilter.tsx";
import { RateCell } from "../components/RateCell.tsx";
import { BUCKET_LABELS, describeLimits, formatContextBand, formatInstant } from "../lib/format.ts";
import { describeShown, usePagedQuery } from "../lib/paging.ts";
import type { ViewProps } from "./PricesView.tsx";

const CHART_BASE_BUCKETS: readonly TokenBucket[] = ["input", "output", "cache_read"];
const CACHE_WRITE_BUCKETS: readonly TokenBucket[] = ["cache_write", "cache_write_5m", "cache_write_1h"];

export function ModelDetailView({ state, update }: ViewProps): React.JSX.Element {
  const [bucket, setBucket] = useState<TokenBucket>("input");
  const [evidence, setEvidence] = useState<{ captureId: string; path: string } | null>(null);
  const captureId = evidence?.captureId ?? null;

  const selected = state.offering_id;
  const listParams = { ...contextParams(state), ...(state.q ? { q: state.q } : {}), limit: 200 };
  const { query: offerings, rows: offeringRows } = usePagedQuery<
    OfferingSummary,
    Awaited<ReturnType<typeof client.offerings>>
  >(
    ["offerings-for-detail", listParams],
    (cursor) => client.offerings((cursor ? { cursor } : listParams) as never),
    { enabled: selected === "" },
  );

  // The offering's identity and current rates, for the heading and rate card.
  const current = useQuery({
    enabled: selected !== "",
    queryKey: ["offering-price", selected, contextParams(state), state.at_utc, state.source_id],
    queryFn: () =>
      client.prices({
        ...contextParams(state),
        offering_id: selected,
        ...(state.at_utc ? { at_utc: state.at_utc } : {}),
        ...(state.source_id ? { source_id: state.source_id } : {}),
      }),
  });

  // The default range ends at the catalog's observation cutoff, which only the
  // server knows. Falling back to the browser clock asked for a range past the
  // cutoff, which the API rightly rejects, on every shared link to this page.
  const cutoff = current.data?.observation_cutoff_utc ?? null;
  const from =
    state.from_utc ||
    (cutoff === null ? "" : new Date(Date.parse(cutoff) - 180 * 86_400_000).toISOString());
  const to = state.to_utc || (cutoff ?? "");

  const windows = useQuery({
    enabled: selected !== "" && from !== "" && to !== "",
    queryKey: ["windows", selected, from, to, contextParams(state)],
    queryFn: () =>
      client.priceWindows({
        offering_id: selected,
        from_utc: from,
        to_utc: to,
        ...contextParams(state),
      } as never),
  });

  const capture = useQuery({
    enabled: captureId !== null,
    queryKey: ["capture", captureId, evidence?.path],
    // Open the excerpt at the table the rate was read from, not at the top of
    // the document, which on a real page is thousands of characters of markup.
    queryFn: () =>
      client.capture(captureId!, { excerpt_limit: 4000, excerpt_path: evidence?.path }),
  });

  if (selected === "") {
    return (
      <section className="panel">
        <h2 className="panel__title">
          Choose an offering{" "}
          {offerings.isSuccess ? (
            <span className="badge">
              {describeShown(offeringRows.length, offerings.hasNextPage, "offering")}
            </span>
          ) : null}
        </h2>
        {offerings.isError ? (
          // A failed list is not an empty catalog; "0 offerings" said it was.
          <p className="notice notice--danger" role="alert" style={{ marginBottom: "var(--space-3)" }}>
            The offerings could not be loaded: {(offerings.error as Error).message}
          </p>
        ) : null}
        <p className="notice">
          Pricing is per offering — who sells the model, under which conditions — so one must be
          selected before a rate or an estimate is shown.
        </p>
        <div className="filters" style={{ marginTop: "var(--space-4)" }}>
          <TextFilter
            label="Search model or alias"
            type="search"
            value={state.q}
            placeholder="claude-sonnet, gpt-4o…"
            onCommit={(value) => update({ q: value }, { replace: true })}
          />
        </div>
        <div className="table-wrap" style={{ marginTop: "var(--space-4)" }}>
          <table>
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col">Sold by</th>
                <th scope="col">Tier</th>
                <th scope="col">Prompt size</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {offeringRows.map((summary) => (
                <tr key={summary.offering.offering_id}>
                  <th scope="row">{summary.model_display_name}</th>
                  <td>{summary.offering.billing_provider_id}</td>
                  <td>{summary.offering.service_tier}</td>
                  <td>{formatContextBand(summary.offering.context_band)}</td>
                  <td>
                    <button
                      type="button"
                      className="button"
                      onClick={() => update({ offering_id: summary.offering.offering_id })}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {offerings.hasNextPage ? (
          <button
            type="button"
            className="button load-more"
            disabled={offerings.isFetchingNextPage}
            onClick={() => void offerings.fetchNextPage()}
          >
            {offerings.isFetchingNextPage ? "Loading…" : "Show more offerings"}
          </button>
        ) : null}
      </section>
    );
  }

  const rows = (windows.data?.data ?? []) as PriceWindow[];
  const sources = [...new Set(rows.map((window) => window.source_id))];
  const priceRow = (current.data?.data?.[0] ?? null) as PriceRow | null;
  const rateSet =
    priceRow?.resolution.status === "matched" ? priceRow.resolution.selected.rate_set : undefined;
  // Chart the cache writes this seller bills: OpenAI's one unsplit rate,
  // Anthropic's per lifetime. A tab for a bucket it does not bill could only
  // report a rate as missing that was never charged.
  const chartBuckets = [
    ...CHART_BASE_BUCKETS,
    ...CACHE_WRITE_BUCKETS.filter((candidate) => rateSet?.[candidate].state !== "not_billed"),
  ];
  const shownBucket = chartBuckets.includes(bucket) ? bucket : "input";
  // The default range ends at the cutoff that comes with the offering's price;
  // if that request failed, the history cannot be asked for at all.
  const historyBlocked = current.isError && (from === "" || to === "");

  return (
    <>
      <section className="panel">
        <button type="button" className="button" onClick={() => update({ offering_id: "" })}>
          ← All offerings
        </button>
        <OfferingSummary
          offeringId={selected}
          row={priceRow}
          pending={current.isPending}
          error={current.isError ? (current.error as Error) : null}
        />
        <div className="filters" style={{ marginTop: "var(--space-4)" }}>
          <TextFilter
            label="History from (UTC)"
            value={state.from_utc}
            placeholder={from.slice(0, 10)}
            validate={validateInstant}
            onCommit={(value) => update({ from_utc: value }, { replace: true })}
          />
          <TextFilter
            label="to (UTC)"
            value={state.to_utc}
            placeholder={to.slice(0, 10)}
            validate={validateInstant}
            onCommit={(value) => update({ to_utc: value }, { replace: true })}
          />
        </div>
      </section>

      <div className="grid-2">
        <section className="panel">
          <h2 className="panel__title">Observed history</h2>
          <div className="tabs" role="group" aria-label="Token bucket to chart">
            {chartBuckets.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className="tab"
                aria-current={shownBucket === candidate ? "page" : undefined}
                onClick={() => setBucket(candidate)}
              >
                {BUCKET_LABELS[candidate]}
              </button>
            ))}
          </div>

          {historyBlocked ? (
            <p className="notice notice--danger" role="alert">
              The history cannot be loaded: its range ends at the catalog&apos;s cutoff, which comes
              with this offering&apos;s price, and that request failed.
            </p>
          ) : windows.isError ? (
            <p className="notice notice--danger" role="alert">
              Could not load this offering's history: {(windows.error as Error).message}
            </p>
          ) : windows.isPending ? (
            <p className="notice">Loading history…</p>
          ) : rows.length === 0 ? (
            <p className="notice notice--empty">
              No price has been observed for this offering in this period.
            </p>
          ) : (
            <HistoryChart windows={rows} bucket={shownBucket} fromUtc={from} toUtc={to} />
          )}

          {rows.length > 0 ? (
            <div className="table-wrap" style={{ marginTop: "var(--space-4)" }}>
              <table>
                <caption className="visually-hidden">
                  Every observed interval, with the source that reported it
                </caption>
                <thead>
                  <tr>
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                    <th scope="col">Source</th>
                    <th scope="col" className="numeric">
                      {BUCKET_LABELS[shownBucket]}
                    </th>
                    <th scope="col">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((window) => (
                    <tr key={window.window_id}>
                      <th scope="row">
                        <StackedInstant value={window.start_utc} />
                      </th>
                      <td>{window.end_utc ? <StackedInstant value={window.end_utc} /> : "open"}</td>
                      <td>{window.source_id}</td>
                      <td className="numeric">
                        <RateCell rate={window.rate_set[shownBucket]} />
                      </td>
                      <td>
                        {window.evidence[shownBucket]?.capture_id ? (
                          <button
                            type="button"
                            className="button"
                            onClick={() =>
                              setEvidence({
                                captureId: window.evidence[shownBucket]!.capture_id,
                                path: window.evidence[shownBucket]!.path,
                              })
                            }
                          >
                            Open capture
                          </button>
                        ) : (
                          <span className="badge">derived</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {sources.length > 1 ? (
            <p className="notice notice--warn" style={{ marginTop: "var(--space-4)" }}>
              {sources.length} sources report this offering. Their observations are kept separate and
              are never averaged; selection picks one whole rule and shows the others as alternatives.
            </p>
          ) : null}
        </section>

        <div style={{ display: "grid", gap: "var(--space-4)", alignContent: "start" }}>
          <EstimateCalculator
            offeringId={selected}
            catalogRevisionId={windows.data?.catalog_revision_id ?? null}
            atUtc={state.at_utc || undefined}
            sourceId={state.source_id || undefined}
            rateSet={rateSet}
          />

          {captureId ? (
            <section className="panel">
              <h2 className="panel__title">Evidence</h2>
              {capture.isPending ? <p className="notice">Loading capture…</p> : null}
              {capture.data ? (
                <>
                  <dl className="context-bar">
                    <div>
                      <dt className="visually-hidden">URL</dt>
                      <dd style={{ margin: 0 }}>
                        <code>{(capture.data.data as SourceCapture).requested_url}</code>
                      </dd>
                    </div>
                    <div>
                      <dt className="visually-hidden">Fetched</dt>
                      <dd style={{ margin: 0 }}>
                        fetched {formatInstant((capture.data.data as SourceCapture).fetched_at_utc)}
                      </dd>
                    </div>
                    <div>
                      <dt className="visually-hidden">Body hash</dt>
                      <dd style={{ margin: 0 }}>
                        sha256 <code>{(capture.data.data as SourceCapture).body_sha256.slice(0, 16)}…</code>
                      </dd>
                    </div>
                  </dl>
                  {/*
                    The archived document is rendered as text. It is never
                    inserted as markup, so a vendor page cannot execute here.
                  */}
                  <pre className="evidence">
                    {capture.data.data.excerpt.text}
                  </pre>
                  <button type="button" className="button" onClick={() => setEvidence(null)}>
                    Close
                  </button>
                </>
              ) : null}
            </section>
          ) : null}
        </div>
      </div>

      {windows.data ? (
        <ContextBar
          catalogRevisionId={windows.data.catalog_revision_id}
          observationSequence={windows.data.observation_sequence}
          observationCutoffUtc={windows.data.observation_cutoff_utc}
          atUtc={state.at_utc || undefined}
        />
      ) : null}
    </>
  );
}

const RATE_CARD_BUCKETS: readonly TokenBucket[] = [
  "input",
  "output",
  "reasoning",
  "cache_read",
  "cache_write_5m",
  "cache_write_1h",
  "cache_write",
];

/**
 * Which offering this page is about, in words, and what it costs now. The
 * opaque ID stays visible but secondary: it is what a consumer pins, not what a
 * person reads.
 */
function OfferingSummary({
  offeringId,
  row,
  pending,
  error,
}: {
  readonly offeringId: string;
  readonly row: PriceRow | null;
  readonly pending: boolean;
  readonly error: Error | null;
}): React.JSX.Element {
  if (error !== null) {
    // A failed request, such as an as-of time past the catalog's cutoff, is
    // not an unknown offering.
    return (
      <>
        <h2 className="offering-title">This offering could not be loaded</h2>
        <p className="notice notice--danger" role="alert" style={{ marginTop: "var(--space-2)" }}>
          {error.message}
        </p>
        <p className="stat__denominator">
          <code>{offeringId}</code>
        </p>
      </>
    );
  }
  if (row === null) {
    return (
      <>
        <h2 className="offering-title">{pending ? "Loading offering…" : "Unknown offering"}</h2>
        <p className="stat__denominator">
          <code>{offeringId}</code>
        </p>
      </>
    );
  }
  const { offering, resolution } = row;
  return (
    <>
      <h2 className="offering-title">{row.model_display_name}</h2>
      <p className="stat__denominator">
        sold by {offering.billing_provider_id} · {offering.service_tier} tier ·{" "}
        {formatContextBand(offering.context_band)} · {offering.region} · <code>{offeringId}</code>
      </p>
      {resolution.status === "matched" ? (
        <>
          <dl className="rate-card" aria-label="Current rates per million tokens">
            {RATE_CARD_BUCKETS.map((candidate) => (
              <div key={candidate}>
                <dt>{BUCKET_LABELS[candidate]}</dt>
                <dd>
                  <RateCell rate={resolution.selected.rate_set[candidate]} />
                </dd>
              </div>
            ))}
          </dl>
          <p className="stat__denominator">
            per million tokens · from {resolution.selected.source_id}, observed since{" "}
            {formatInstant(resolution.selected.start_utc)}
            {resolution.alternatives.length > 0
              ? ` · ${resolution.alternatives.length} other source(s) report this offering`
              : ""}
          </p>
          <p className="offering-limits">
            {describeLimits(resolution.selected.limits).length > 0 ? (
              <>
                {describeLimits(resolution.selected.limits)
                  .map((line) => line.long)
                  .join(" · ")}
                <span className="stat__denominator">
                  {" "}
                  · from {resolution.selected.limits_source_id}
                </span>
              </>
            ) : (
              <span className="stat__denominator">No source states this model&apos;s token limits.</span>
            )}
          </p>
        </>
      ) : (
        <p className="notice notice--warn" style={{ marginTop: "var(--space-3)" }}>
          No accepted price at this time (
          {resolution.status === "not_found" ? resolution.reason : resolution.status}).
        </p>
      )}
    </>
  );
}

/**
 * A UTC instant as a date with its time beneath. Two full timestamps side by
 * side pushed the evidence button out of the history table.
 */
function StackedInstant({ value }: { readonly value: string }): React.JSX.Element {
  return (
    <span title={formatInstant(value)}>
      {value.slice(0, 10)}
      <span className="stat__denominator" style={{ display: "block", marginTop: 0 }}>
        {value.slice(11, 16)} UTC
      </span>
    </span>
  );
}
